import { apiClient } from '../apiClient';

/**
 * Helper to extract company slug from current URL path
 * CRITICAL: Returns empty string if company not found to prevent malformed URLs
 */
const getCompanySlug = (): string => {
  if (typeof window === 'undefined') return '';
  const parts = window.location.pathname.split('/').filter(Boolean);
  const company = parts[0] || '';
  
  // CRITICAL FIX: Validate company slug to prevent empty/malformed URLs
  // Empty company would result in /api//rpc/clients/list which causes 404
  if (!company || company === 'undefined' || company.trim() === '') {
    console.warn('[rpcClients] Company slug is empty or invalid:', {
      pathname: window.location.pathname,
      parts,
    });
    return '';
  }
  
  return company;
};

/**
 * Build RPC endpoint URL
 * CRITICAL: Validates company before building URL to prevent 404s
 */
const buildUrl = (path: string): string => {
  const company = getCompanySlug();
  
  // CRITICAL FIX: Prevent API calls with empty company (would cause 404)
  if (!company) {
    const error = new Error('Company slug is required but not available. Please ensure you are on a valid company page.');
    console.error('[rpcClients] buildUrl failed:', error.message, {
      pathname: window.location.pathname,
    });
    throw error; // Throw error to prevent malformed request
  }
  
  return `/api/${company}/rpc/clients${path}`;
};

/**
 * RPC client for client-related operations
 * All endpoints return { ok: true, data } or { ok: false, error }
 */
const rpcClients = {
  /**
   * List all admin clients
   * @param options - Optional: limit, page, search
   */
  async list(options?: { limit?: number; page?: number; search?: string }) {
    const { data } = await apiClient.post(buildUrl('/list'), options || {});
    return data?.ok ? data.data : (Array.isArray(data) ? data : []);
  },

  /**
   * Get client by ID (admin view)
   */
  getById(clientId: string) {
    return apiClient.post(buildUrl('/get-by-id'), { clientId });
  },

  /**
   * Update client as admin
   */
  updateAdmin(clientId: string, payload: any) {
    return apiClient.post(buildUrl('/update-admin'), { clientId, payload });
  },

  /**
   * Update client as manager
   */
  updateManager(clientId: string, payload: any) {
    return apiClient.post(buildUrl('/update-manager'), { clientId, payload });
  },

  /**
   * Update client as agent
   */
  updateAgent(clientId: string, payload: any) {
    return apiClient.post(buildUrl('/update-agent'), { clientId, payload });
  },

  /**
   * Block/unblock client
   */
  block(clientId: string, isBlocked: boolean) {
    return apiClient.post(buildUrl('/block'), { clientId, isBlocked });
  },

  /**
   * Convert client to potential
   */
  convertToPotential(clientId: string, agentId?: string) {
    return apiClient.post(buildUrl('/convert-to-potential'), { clientId, agentId });
  },

  /**
   * Add manager to client
   */
  addManager(clientId: string, managerId: string) {
    return apiClient.post(buildUrl('/add-manager'), { clientId, managerId });
  },

  /**
   * Remove manager from client
   */
  removeManager(clientId: string, managerId: string) {
    return apiClient.post(buildUrl('/remove-manager'), { clientId, managerId });
  },

  /**
   * List client notes
   */
  async notesList(clientId: string) {
    const { data } = await apiClient.post(buildUrl('/notes/list'), { clientId });
    return data?.ok ? data.data : (data?.notes ? data.notes : (Array.isArray(data) ? data : []));
  },

  /**
   * Create client note
   * Note: Increased timeout to 30s due to storage checks and multiple DB operations
   */
  notesCreate(clientId: string, text: string) {
    return apiClient.post(buildUrl('/notes/create'), { clientId, text }, {
      timeout: 30000, // 30 seconds - storage checks and multiple DB queries can take time
    });
  },

  /**
   * Update client note
   */
  notesUpdate(clientId: string, noteIdx: number, text: string) {
    return apiClient.post(buildUrl('/notes/update'), { clientId, noteIdx, text });
  },

  /**
   * Delete client note
   */
  notesDelete(clientId: string, noteIdx: number) {
    return apiClient.post(buildUrl('/notes/delete'), { clientId, noteIdx });
  },

  /**
   * List potential clients
   */
  potentialClientsList(agentId?: string, page: number = 1, pageSize: number = 10, isBlocked?: boolean) {
    return apiClient.post(buildUrl('/potential-clients/list'), { agentId, page, pageSize, isBlocked });
  },

  /**
   * Get potential client by ID
   */
  potentialClientsGetById(clientId: string) {
    return apiClient.post(buildUrl('/potential-clients/get-by-id'), { clientId });
  },

  /**
   * Update potential client
   */
  potentialClientsUpdate(clientId: string, updateData: any) {
    return apiClient.post(buildUrl('/potential-clients/update'), { clientId, updateData });
  },

  /**
   * Update potential client status
   */
  potentialClientsUpdateStatus(clientId: string, status: string) {
    return apiClient.post(buildUrl('/potential-clients/update-status'), { clientId, status });
  },

  /**
   * Create potential client
   */
  potentialClientsCreate(payload: {
    fullName: string;
    phone?: string;
    email?: string;
    description?: string;
    addedByAgent?: string;
    business?: any;
  }) {
    return apiClient.post(buildUrl('/potential-clients/create'), payload);
  },

  /**
   * Block/unblock potential client
   */
  potentialClientsBlock(clientId: string, isBlocked: boolean) {
    return apiClient.post(buildUrl('/potential-clients/block'), { clientId, isBlocked });
  },

  /**
   * Convert potential client to regular client
   */
  potentialClientsConvert(clientId: string) {
    return apiClient.post(buildUrl('/potential-clients/convert'), { clientId });
  },

  /**
   * Transfer potential client to another agent
   */
  potentialClientsTransfer(clientId: string, newAgentId: string) {
    return apiClient.post(buildUrl('/potential-clients/transfer'), { clientId, newAgentId });
  },

  /**
   * Delete potential client
   */
  potentialClientsDelete(clientId: string) {
    return apiClient.post(buildUrl('/potential-clients/delete'), { clientId });
  },

  /**
   * Send notification
   */
  notify(clientId: string, target: 'admin' | 'agent', action: string, senderName?: string, timestamp?: string) {
    return apiClient.post(buildUrl('/notify'), { clientId, target, action, senderName, timestamp });
  },

  /**
   * Notify admin about manager actions
   */
  notifyAdmin(payload: {
    clientId?: string;
    managerName: string;
    action: 'added' | 'removed' | 'task_note';
    timestamp?: string;
    taskId?: string;
    taskPublicId?: string;
    taskTitle?: string;
    noteId?: string;
    visibility?: 'all' | 'admin' | 'manager';
  }) {
    return apiClient.post(buildUrl('/notify-admin'), payload);
  },

  /**
   * Update client password
   */
  updateClientPassword(clientId: string, newPassword: string) {
    return apiClient.post(buildUrl('/update-client-password'), { clientId, newPassword });
  },

  /**
   * Delete admin client
   */
  deleteAdminClient(clientId: string) {
    return apiClient.post(buildUrl('/delete-admin-client'), { clientId });
  },

  /**
   * Create client payment
   */
  createClientPayment(clientId: string, payload: { projectName: string; type: string; bankDetails?: any; paymentDates?: any[]; balance?: number; debt?: number }) {
    return apiClient.post(buildUrl('/create-client-payment'), { clientId, ...payload });
  },

  /**
   * Renew client
   */
  renewClient(clientId: string, payload: { subscriptionType?: string; totalSessions?: number; startDate?: Date | string; endDate?: Date | string }) {
    return apiClient.post(buildUrl('/renew-client'), { clientId, ...payload });
  },

  /**
   * Get custom fields
   */
  getCustomFields(clientId: string) {
    return apiClient.post(buildUrl('/get-custom-fields'), { clientId });
  },

  /**
   * Create custom field
   */
  createCustomField(clientId: string, payload: { label: string; type: string; value?: any; secret?: boolean; order?: number; key?: string }) {
    return apiClient.post(buildUrl('/create-custom-field'), { clientId, ...payload });
  },

  /**
   * Update custom field
   */
  updateCustomField(clientId: string, fieldId: string, payload: { label?: string; type?: string; value?: any; secret?: boolean; order?: number }) {
    return apiClient.post(buildUrl('/update-custom-field'), { clientId, fieldId, ...payload });
  },

  /**
   * Delete custom field
   */
  deleteCustomField(clientId: string, fieldId: string) {
    return apiClient.post(buildUrl('/delete-custom-field'), { clientId, fieldId });
  },

  /**
   * Get contacts
   */
  getContacts(clientId: string) {
    return apiClient.post(buildUrl('/get-contacts'), { clientId });
  },

  /**
   * Add contact
   */
  addContact(clientId: string, payload: { name?: string; email: string; note?: string }) {
    return apiClient.post(buildUrl('/add-contact'), { clientId, ...payload });
  },

  /**
   * Delete contact
   */
  deleteContact(clientId: string, contactId: string) {
    return apiClient.post(buildUrl('/delete-contact'), { clientId, contactId });
  },

  // ==================== Category 1: Clients Base Operations ====================

  /**
   * Get admin clients list
   */
  getAdminClients(params?: Record<string, any>) {
    return apiClient.post(buildUrl('/get-admin-clients'), { params });
  },

  /**
   * Get manager clients list
   */
  getManagerClients() {
    return apiClient.post(buildUrl('/get-manager-clients'), {});
  },

  /**
   * Get manager client by ID
   */
  getManagerClientById(clientId: string) {
    return apiClient.post(buildUrl('/get-manager-client-by-id'), { clientId });
  },

  // ==================== Category 2: Client Import Operations ====================

  /**
   * Upload clients import file
   */
  uploadClientsImport(formData: FormData, lang: string = 'he') {
    // Note: FormData needs special handling - may need to use multipart/form-data
    return apiClient.post(buildUrl('/upload-clients-import'), formData, {
      params: { lang },
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  /**
   * Run clients import dry run
   */
  runClientsImportDryRun(uploadId: string, mapping: any, options: any) {
    return apiClient.post(buildUrl('/run-clients-import-dry-run'), { uploadId, mapping, options });
  },

  /**
   * Commit clients import
   */
  commitClientsImport(uploadId: string) {
    return apiClient.post(buildUrl('/commit-clients-import'), { uploadId });
  },

  // ==================== Category 3: Authentication & Profile ====================

  /**
   * Get auth me
   */
  getAuthMe() {
    return apiClient.post(buildUrl('/get-auth-me'), {});
  },

  /**
   * Get client me
   */
  getClientMe() {
    return apiClient.post(buildUrl('/get-client-me'), {});
  },

  /**
   * Change client password (client-facing)
   */
  changeClientPassword(payload: { currentPassword?: string; newPassword: string; [key: string]: any }) {
    return apiClient.post(buildUrl('/change-client-password'), payload);
  },

  /**
   * Send email change OTP
   */
  sendEmailChangeOtp(newEmail: string) {
    return apiClient.post(buildUrl('/send-email-change-otp'), { newEmail });
  },

  /**
   * Change client email
   */
  changeClientEmail(payload: { newEmail: string; otp: string }) {
    return apiClient.post(buildUrl('/change-client-email'), payload);
  },

  /**
   * Update client phone
   */
  updateClientPhone(phone: string) {
    return apiClient.post(buildUrl('/update-client-phone'), { phone });
  },

  /**
   * Send 2FA setup OTP
   */
  send2FASetupOtp(payload?: { method?: 'email' | 'sms' }) {
    return apiClient.post(buildUrl('/send-2fa-setup-otp'), payload || {});
  },

  /**
   * Toggle 2FA
   */
  toggle2FA(payload: { enabled: boolean; otp?: string }) {
    return apiClient.post(buildUrl('/toggle-2fa'), payload);
  },

  /**
   * Toggle login alert
   */
  toggleLoginAlert(enabled: boolean) {
    return apiClient.post(buildUrl('/toggle-login-alert'), { enabled }, {
      timeout: 5000, // 5 second timeout for fast error detection
    });
  },

  /**
   * Update client preferences
   */
  updateClientPreferences(preferences: any) {
    return apiClient.post(buildUrl('/update-client-preferences'), { preferences });
  },

  /**
   * Register client
   * Note: Increased timeout to 30s due to storage checks and database operations
   */
  registerClient(payload: any) {
    return apiClient.post(buildUrl('/register-client'), payload, {
      timeout: 30000, // 30 seconds - client registration involves storage checks, DB operations, and validation
    });
  },

  /**
   * Send invitation email to newly created user
   * Note: Email sending may take time (RabbitMQ queue or direct send), so timeout is increased
   */
  async sendInvitation(payload: {
    email: string;
    fullName: string;
    role: string;
    password: string;
    companyName: string;
    companySlug: string;
  }) {
    try {
      const { data } = await apiClient.post(buildUrl('/send-invitation'), payload, {
        timeout: 30000, // 30 seconds - email sending may involve RabbitMQ queue or direct Gmail API calls
      });
      // Check if response indicates success
      if (data?.ok === false || data?.error) {
        const error = new Error(data.error || 'Failed to send invitation email');
        (error as any).response = {
          status: 400,
          data: { error: data.error || 'Failed to send invitation email' }
        };
        throw error;
      }
      return data?.ok ? data.data : data;
    } catch (err: any) {
      // Check if it's a timeout error
      const isTimeout = err?.code === 'ECONNABORTED' || 
                        err?.message?.includes('timeout') || 
                        err?.message?.includes('Timeout') ||
                        err?.config?.timeout !== undefined;
      
      if (isTimeout) {
        // For timeout errors, assume the email was sent (it's likely in a queue)
        // Return a success-like response with a note about timeout
        console.warn('[rpcClients.sendInvitation] Request timed out, but email may have been sent:', {
          email: payload.email,
          timeout: err?.config?.timeout
        });
        // Return a response that indicates partial success (email likely sent but confirmation timed out)
        return {
          message: 'Invitation email queued (confirmation timed out)',
          timeout: true,
          email: payload.email
        };
      }
      
      // Re-throw if it's already an error with response
      if (err.response) {
        throw err;
      }
      // Wrap in a structure that matches the expected error format
      const error = new Error(err?.message || 'Failed to send invitation email');
      (error as any).response = {
        status: err?.status || 500,
        data: { error: err?.message || 'Failed to send invitation email' }
      };
      throw error;
    }
  },

  // ==================== Category 4: Client Orders ====================

  /**
   * Get client orders
   */
  async getClientOrders() {
    const { data } = await apiClient.post(buildUrl('/get-client-orders'), {});
    return data?.ok ? data.data : (Array.isArray(data) ? data : []);
  },

  /**
   * Accept client order
   */
  acceptClientOrder(orderId: string) {
    return apiClient.post(buildUrl('/accept-client-order'), { orderId });
  },

  /**
   * Cancel order as client
   */
  cancelOrderAsClient(orderId: string) {
    return apiClient.post(buildUrl('/cancel-order-as-client'), { orderId });
  },

  /**
   * Get admin orders for client
   */
  async getAdminOrdersForClient(clientId: string) {
    try {
      const { data } = await apiClient.post(buildUrl('/get-admin-orders-for-client'), { clientId });
      if (data?.ok) {
        return Array.isArray(data.data) ? data.data : [];
      }
      return Array.isArray(data) ? data : [];
    } catch (err) {
      console.error('Error fetching admin orders for client:', err);
      return [];
    }
  },

  /**
   * Create admin order
   */
  createAdminOrder(payload: any) {
    return apiClient.post(buildUrl('/create-admin-order'), payload);
  },

  /**
   * Post admin order action (generic helper)
   */
  postAdminOrderAction(orderId: string, path: string, body?: any) {
    return apiClient.post(buildUrl('/post-admin-order-action'), { orderId, path, body });
  },

  // ==================== Category 5: Admin Client Notes ====================

  /**
   * Add admin client note
   */
  addAdminClientNote(clientId: string, text: string) {
    return apiClient.post(buildUrl('/add-admin-client-note'), { clientId, text });
  },

  /**
   * Get admin client notes
   */
  async getAdminClientNotes(clientId: string) {
    const { data } = await apiClient.post(buildUrl('/get-admin-client-notes'), { clientId });
    return data?.ok ? data.data : (data?.notes ? data.notes : (Array.isArray(data) ? data : []));
  },

  /**
   * Update admin client note
   */
  updateAdminClientNote(clientId: string, noteIdx: number, text: string) {
    return apiClient.post(buildUrl('/update-admin-client-note'), { clientId, noteIdx, text });
  },

  /**
   * Delete admin client note
   */
  deleteAdminClientNote(clientId: string, noteIdx: number) {
    return apiClient.post(buildUrl('/delete-admin-client-note'), { clientId, noteIdx });
  },

  /**
   * Get admin client payments
   */
  async getAdminClientPayments(clientId: string) {
    const { data } = await apiClient.post(buildUrl('/get-admin-client-payments'), { clientId });
    return data?.ok ? data.data : (data?.payments ? data.payments : (Array.isArray(data) ? data : []));
  },

  /**
   * Add admin client payment
   */
  addAdminClientPayment(clientId: string, payload: { projectName: string; type: string; bankDetails?: any; paymentDates?: any[]; balance?: number; debt?: number }) {
    return apiClient.post(buildUrl('/add-admin-client-payment'), { clientId, ...payload });
  },

  /**
   * Update admin client payment
   */
  updateAdminClientPayment(clientId: string, paymentId: string, payload: any) {
    return apiClient.post(buildUrl('/update-admin-client-payment'), { clientId, paymentId, ...payload });
  },

  /**
   * Delete admin client payment
   */
  deleteAdminClientPayment(clientId: string, paymentId: string) {
    return apiClient.post(buildUrl('/delete-admin-client-payment'), { clientId, paymentId });
  },

  // ==================== Category 6: Admin Agents' Clients ====================

  /**
   * Get admin agents clients
   */
  getAdminAgentsClients(params?: Record<string, any>) {
    return apiClient.post(buildUrl('/get-admin-agents-clients'), { params });
  },

  /**
   * Get admin agent client by ID
   */
  getAdminAgentClientById(id: string) {
    return apiClient.post(buildUrl('/get-admin-agent-client-by-id'), { id });
  },

  /**
   * Set admin agent client blocked
   */
  setAdminAgentClientBlocked(id: string, isBlocked: boolean, method: 'patch' | 'put' = 'patch') {
    return apiClient.post(buildUrl('/set-admin-agent-client-blocked'), { id, isBlocked, method });
  },

  /**
   * Update admin agent client status
   */
  updateAdminAgentClientStatus(id: string, status: string) {
    return apiClient.post(buildUrl('/update-admin-agent-client-status'), { id, status });
  },

  /**
   * Convert admin agent client
   */
  convertAdminAgentClient(id: string) {
    return apiClient.post(buildUrl('/convert-admin-agent-client'), { id });
  },

  /**
   * Transfer admin agent client
   */
  transferAdminAgentClient(id: string, newAgentId: string) {
    return apiClient.post(buildUrl('/transfer-admin-agent-client'), { id, newAgentId });
  },

  /**
   * Delete admin agent client
   */
  deleteAdminAgentClient(id: string) {
    return apiClient.post(buildUrl('/delete-admin-agent-client'), { id });
  },

  // ==================== Category 7: Inventory ====================

  /**
   * Get admin inventory
   */
  getAdminInventory() {
    return apiClient.post(buildUrl('/get-admin-inventory'), {});
  },

  // ==================== Category 8: Attachments & Invoices ====================

  /**
   * Get invoice folders
   */
  getInvoiceFolders(clientId: string) {
    return apiClient.post(buildUrl('/get-invoice-folders'), { clientId });
  },

  /**
   * Get client attachments
   */
  getClientAttachments(clientId: string, folder: string, params?: Record<string, string | undefined>) {
    return apiClient.post(buildUrl('/get-client-attachments'), { clientId, folder, params });
  },

  /**
   * Upload client attachment
   */
  uploadClientAttachment(
    clientId: string, 
    folder: string, 
    formData: FormData, 
    subfolder?: string,
    onUploadProgress?: (progressEvent: { loaded: number; total: number }) => void
  ) {
    // Append required fields to FormData (backend expects them in body, not query params)
    formData.append('clientId', clientId);
    formData.append('folder', folder);
    if (subfolder) {
      formData.append('subfolder', subfolder);
    }
    return apiClient.post(buildUrl('/upload-client-attachment'), formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 600000, // 10 minutes timeout for large file uploads
      onUploadProgress: onUploadProgress ? (progressEvent) => {
        if (progressEvent.total) {
          onUploadProgress({
            loaded: progressEvent.loaded,
            total: progressEvent.total,
          });
        }
      } : undefined,
    });
  },

  /**
   * Get Cloudinary upload signature for direct upload
   */
  getUploadSignature(folder: string, subfolder?: string, resource_type: string = 'raw', type: string = 'upload') {
    return apiClient.post(buildUrl('/get-upload-signature'), { folder, subfolder, resource_type, type });
  },

  /**
   * Save attachment metadata after direct Cloudinary upload
   */
  saveAttachmentMetadata(data: {
    clientId: string;
    folder: string;
    subfolder?: string;
    title: string;
    public_id: string;
    url: string;
    bytes: number;
    format?: string;
    mime?: string;
    resource_type: string;
  }) {
    return apiClient.post(buildUrl('/save-attachment-metadata'), data);
  },

  /**
   * Delete client attachment
   */
  deleteClientAttachment(clientId: string, publicId: string) {
    return apiClient.post(buildUrl('/delete-client-attachment'), { clientId, publicId });
  },

  /**
   * Download attachments zip
   */
  downloadAttachmentsZip(params: Record<string, any>) {
    return apiClient.post(buildUrl('/download-attachments-zip'), params, {
      responseType: 'blob',
    });
  },

  /**
   * Send attachments zip
   */
  sendAttachmentsZip(payload: any) {
    return apiClient.post(buildUrl('/send-attachments-zip'), payload);
  },

  // ==================== Category 9: Bulk Operations & Templates ====================

  /**
   * Get bulk templates
   */
  getBulkTemplates(kind: 'sms' | 'email') {
    return apiClient.post(buildUrl('/get-bulk-templates'), { kind });
  },

  /**
   * Send bulk SMS
   */
  sendBulkSms(payload: any) {
    // CRITICAL: Backend now returns 202 immediately after queuing
    // Reduced timeout to 10 seconds (backend should respond in 1-2s)
    return apiClient.post(buildUrl('/send-bulk-sms'), payload, {
      timeout: 10000, // 10 seconds - backend returns 202 immediately
    });
  },

  /**
   * Send bulk email
   */
  sendBulkEmail(payload: any) {
    // CRITICAL: Bulk email operations can take longer - increase timeout to 60 seconds
    return apiClient.post(buildUrl('/send-bulk-email'), payload, {
      timeout: 60000, // 60 seconds for bulk operations
    });
  },

  /**
   * Unified messaging: Send SMS or WhatsApp
   */
  sendMessage(payload: {
    channel: 'SMS' | 'WHATSAPP';
    toPhone: string;
    text?: string;
    templateName?: string;
    templateId?: number;
    variables?: Record<string, string> | Array<{ id: number; value: string }>;
    context?: { type: string; id?: string; metadata?: any };
  }) {
    return apiClient.post(buildUrl('/messaging/send'), payload);
  },

  /**
   * List message logs with filters
   */
  listMessageLogs(payload: {
    page?: number;
    limit?: number;
    channel?: 'SMS' | 'WHATSAPP' | null;
    status?: 'sent' | 'failed' | 'queued' | 'delivered' | 'bounced' | 'unsubscribed' | null;
    direction?: 'outbound' | 'inbound' | null;
    toPhone?: string | null;
    clientId?: string | null;
    fromDate?: string | null;
    toDate?: string | null;
    search?: string | null;
  }) {
    return apiClient.post(buildUrl('/messaging/logs/list'), payload);
  },

  /**
   * Get single message log by ID
   */
  getMessageLog(id: string) {
    return apiClient.get(buildUrl(`/messaging/logs/${id}`));
  },

  /**
   * Create template
   */
  createTemplate(payload: { channel: 'sms' | 'email'; title: string; body?: string; subject?: string }) {
    return apiClient.post(buildUrl('/create-template'), payload);
  },

  /**
   * Get admin features
   */
  getAdminFeatures() {
    return apiClient.post(buildUrl('/get-admin-features'), {});
  },

  /**
   * Get SMS limit
   */
  getSmsLimit() {
    return apiClient.post(buildUrl('/get-sms-limit'), {});
  },

  // ==================== Category 10: Managers & Agents ====================

  /**
   * Get admin managers
   */
  getAdminManagers() {
    return apiClient.post(buildUrl('/get-admin-managers'), {});
  },

  /**
   * Get manager managers
   */
  getManagerManagers() {
    return apiClient.post(buildUrl('/get-manager-managers'), {});
  },

  /**
   * Get admin agents
   */
  getAdminAgents() {
    return apiClient.post(buildUrl('/get-admin-agents'), {});
  },

  // ==================== Category 11: Reports & Google ====================

  /**
   * Export usage report (now returns jobId for async processing)
   * The backend queues the export job and returns a jobId
   */
  async exportUsageReport(type: 'csv' | 'xlsx' | 'pdf'): Promise<{ ok: boolean; data?: { jobId: string; message: string }; error?: string }> {
    const { data } = await apiClient.post(buildUrl('/export-usage-report'), { type });
    return data;
  },

  /**
   * Get job status
   */
  async getJobStatus(jobId: string): Promise<{ ok: boolean; data: { jobId: string; status: string; progress: number; result?: { url: string; filename: string; mimetype: string }; error?: string } }> {
    const { data } = await apiClient.post(buildUrl('/jobs/status'), { jobId });
    return data;
  },

  /**
   * Download completed export file
   */
  async downloadJobFile(jobId: string): Promise<{ data: Blob }> {
    const response = await apiClient.post(buildUrl('/jobs/download'), { jobId }, {
      responseType: 'blob',
    });
    return { data: response.data };
  },

  /**
   * Get Google status
   */
  getGoogleStatus(params?: { adminId?: string; managerId?: string }) {
    return apiClient.post(buildUrl('/get-google-status'), { params });
  },

  // ==================== Category 12: Tasks & Reminders ====================

  /**
   * Get task meta
   */
  getTaskMeta() {
    return apiClient.post(buildUrl('/get-task-meta'), {});
  },

  /**
   * Get client reminders
   */
  getClientReminders(clientId: string) {
    return apiClient.post(buildUrl('/get-client-reminders'), { clientId });
  },

  /**
   * Get tasks by client
   */
  getTasksByClient(clientId: string, rolePrefix: 'admin' | 'manager') {
    return apiClient.post(buildUrl('/get-tasks-by-client'), { clientId, rolePrefix });
  },

  /**
   * Get admin tasks by client
   */
  getAdminTasksByClient(clientId: string) {
    return apiClient.post(buildUrl('/get-admin-tasks-by-client'), { clientId });
  },

  /**
   * Get client tasks
   */
  getClientTasks(clientId: string, rolePrefix: 'admin' | 'manager') {
    return apiClient.post(buildUrl('/get-client-tasks'), { clientId, rolePrefix });
  },

  /**
   * Update task status
   */
  updateTaskStatus(taskId: string, rolePrefix: 'admin' | 'manager', status: string) {
    return apiClient.post(buildUrl('/update-task-status'), { taskId, rolePrefix, status });
  },

  /**
   * Transfer task manager
   */
  transferTaskManager(taskId: string, rolePrefix: 'admin' | 'manager', managerId: string) {
    return apiClient.post(buildUrl('/transfer-task-manager'), { taskId, rolePrefix, managerId });
  },

  /**
   * Get task by ID
   */
  getTaskById(taskId: string, rolePrefix: 'admin' | 'manager') {
    return apiClient.post(buildUrl('/get-task-by-id'), { taskId, rolePrefix });
  },

  /**
   * Get admin task by ID
   */
  getAdminTaskById(taskId: string) {
    return apiClient.post(buildUrl('/get-admin-task-by-id'), { taskId });
  },

  /**
   * Get reminder by ID
   */
  getReminderById(reminderId: string) {
    return apiClient.post(buildUrl('/get-reminder-by-id'), { reminderId });
  },

  /**
   * Update reminder
   */
  updateReminder(taskId: string, payload: any) {
    return apiClient.post(buildUrl('/update-reminder'), { taskId, payload });
  },

  /**
   * Complete task
   */
  completeTask(taskId: string, rolePrefix: 'admin' | 'manager') {
    return apiClient.post(buildUrl('/complete-task'), { taskId, rolePrefix });
  },

  /**
   * Complete admin task
   */
  completeAdminTask(taskId: string) {
    return apiClient.post(buildUrl('/complete-admin-task'), { taskId });
  },

  /**
   * Create reminder
   */
  createReminder(payload: any) {
    return apiClient.post(buildUrl('/create-reminder'), payload);
  },
};

export default rpcClients;

